import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Upload, X, CheckCircle2, AlertCircle } from 'lucide-react';
import { toast } from 'react-toastify';

import ApiServices from '../../shared/ApiServices/ApiServices';
import PathologyServices from '../../shared/ApiServices/PathologyServices';
import { Dialog, Button } from '../../shared/custom-ui';
import { buildPatientDisplayName } from '../../shared/utils/nameUtils';

type UploadStage =
    | 'idle'
    | 'creating'
    | 'presigning'
    | 'uploading'
    | 'completing'
    | 'done'
    | 'error';

interface PatientOption {
    patientID: string;
    name: string;
}

interface FileItem {
    id: string;
    file: File;
    progress: number;
    status: 'pending' | 'uploading' | 'done' | 'error';
    error?: string;
}

interface PathologyUploadModalProps {
    onUploadFinished?: (uploadId: string) => void;
    /** When set, skips the patient picker and uses this patient directly */
    preselectedPatientId?: string;
    /** Display name shown when preselectedPatientId is set */
    preselectedPatientName?: string;
    /** The ID of the order to link the upload to */
    preselectedOrderId?: string;
}

const ALLOWED_EXTENSIONS = ['dcm', 'zip'];
const ACCEPT_ATTR = '.dcm,.zip';

const fileExt = (name: string) => (name.split('.').pop() || '').toLowerCase();

const inferUploadFileType = (files: File[]): '.dcm' | '.zip' | null => {
    if (!files.length) return null;
    const allExt = files.map(f => fileExt(f.name));
    if (allExt.every(ext => ext === 'dcm')) return '.dcm';
    if (allExt.every(ext => ext === 'zip')) return '.zip';
    return null;
};

const formatBytes = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    const units = ['KB', 'MB', 'GB', 'TB'];
    let v = bytes / 1024;
    let i = 0;
    while (v >= 1024 && i < units.length - 1) {
        v /= 1024;
        i += 1;
    }
    return `${v.toFixed(v >= 100 ? 0 : v >= 10 ? 1 : 2)} ${units[i]}`;
};

const makeFileItem = (file: File): FileItem => ({
    id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
    file,
    progress: 0,
    status: 'pending',
});

const PathologyUploadModal: React.FC<PathologyUploadModalProps> = ({
    onUploadFinished,
    preselectedPatientId,
    preselectedPatientName,
    preselectedOrderId,
}) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [open, setOpen] = useState(false);

    const [patients, setPatients] = useState<PatientOption[]>([]);
    const [patientsLoading, setPatientsLoading] = useState(false);
    // When a patient is preselected via props, start with that value
    const [selectedPatient, setSelectedPatient] = useState(preselectedPatientId ?? '');



    const [items, setItems] = useState<FileItem[]>([]);
    const [stage, setStage] = useState<UploadStage>('idle');
    const [errorMsg, setErrorMsg] = useState('');
    const [uploadId, setUploadId] = useState('');

    const uploading = stage === 'creating' || stage === 'presigning' || stage === 'uploading' || stage === 'completing';

    const totalProgress = useMemo(() => {
        if (!items.length) return 0;
        const sum = items.reduce((acc, it) => acc + it.progress, 0);
        return Math.round(sum / items.length);
    }, [items]);

    const stageLabel = useMemo(() => {
        switch (stage) {
            case 'creating':
                return 'Creating pathology record...';
            case 'presigning':
                return 'Generating secure upload URLs...';
            case 'uploading':
                return 'Uploading files to storage...';
            case 'completing':
                return 'Finalizing upload...';
            case 'done':
                return 'Upload completed';
            case 'error':
                return 'Upload failed';
            default:
                return '';
        }
    }, [stage]);

    useEffect(() => {
        // Only fetch all patients if no patient was preselected
        if (!open || preselectedPatientId) return;

        const run = async () => {
            setPatientsLoading(true);
            setErrorMsg('');
            try {
                const res = await ApiServices.getAllPatients();
                const list: PatientOption[] = (res?.items || []).map((p: any) => ({
                    patientID: p.PK,
                    name: buildPatientDisplayName(p) || p.PK,
                }));
                setPatients(list);
                if (list.length && !selectedPatient) {
                    setSelectedPatient(list[0].patientID);
                }
            } catch {
                setErrorMsg('Failed to load patients');
                toast.error('Failed to load patients');
            } finally {
                setPatientsLoading(false);
            }
        };

        run();
    }, [open, preselectedPatientId]);

    // Keep selectedPatient in sync if the prop changes externally
    useEffect(() => {
        if (preselectedPatientId) setSelectedPatient(preselectedPatientId);
    }, [preselectedPatientId]);

    const resetAll = useCallback(() => {
        setItems([]);
        setStage('idle');
        setErrorMsg('');
        setUploadId('');
    }, []);

    useEffect(() => {
        if (stage !== 'done') return;
        const t = window.setTimeout(() => {
            setOpen(false);
            resetAll();
        }, 1400);
        return () => window.clearTimeout(t);
    }, [stage, resetAll]);

    const closeModal = () => {
        if (uploading) return;
        setOpen(false);
        resetAll();
    };

    const onPickFiles = (incoming: FileList | null) => {
        if (!incoming || incoming.length === 0) return;

        const picked = Array.from(incoming);
        const invalid = picked.find(f => !ALLOWED_EXTENSIONS.includes(fileExt(f.name)));
        if (invalid) {
            toast.error(`Unsupported file: ${invalid.name}. Only .dcm or .zip allowed.`);
            return;
        }

        setItems(prev => [...prev, ...picked.map(makeFileItem)]);
    };

    const onDrop: React.DragEventHandler<HTMLDivElement> = (e) => {
        e.preventDefault();
        if (uploading) return;
        onPickFiles(e.dataTransfer.files);
    };

    const removeItem = (id: string) => {
        if (uploading) return;
        setItems(prev => prev.filter(it => it.id !== id));
    };

    const clearFiles = () => {
        if (uploading) return;
        setItems([]);
    };

    const startUpload = async () => {
        setErrorMsg('');
        if (!selectedPatient) {
            toast.error('Please select a patient');
            return;
        }

        if (items.length === 0) {
            toast.error('Please add at least one file');
            return;
        }

        const files = items.map(i => i.file);
        const uploadFileType = inferUploadFileType(files);

        if (!uploadFileType) {
            const msg = 'Mixed file types are not allowed in one upload. Use either all .dcm or all .zip files.';
            setErrorMsg(msg);
            toast.error(msg);
            return;
        }

        const normalizedPatientId = selectedPatient.startsWith('PATIENT#')
            ? selectedPatient
            : `PATIENT#${selectedPatient}`;

        try {
            setStage('creating');
            const createRes = await PathologyServices.createPathologyRecord(normalizedPatientId, {
                priority: 'routine',
                orderId: preselectedOrderId,
            });
            const newUploadId = createRes.uploadId;
            if (!newUploadId) throw new Error('Backend did not return uploadId');
            setUploadId(newUploadId);

            setStage('presigning');
            const presignedList = await PathologyServices.generatePathologyPresignedUrl(
                newUploadId,
                files.length,
                uploadFileType
            );

            if (!presignedList.length || presignedList.length !== files.length) {
                throw new Error('Presigned URL response count mismatch');
            }

            setStage('uploading');

            const updateProgress = (id: string, pct: number) => {
                setItems(prev =>
                    prev.map(it => {
                        if (it.id !== id) return it;
                        return { ...it, progress: pct, status: pct >= 100 ? 'done' : 'uploading' };
                    })
                );
            };

            const markError = (id: string, msg: string) => {
                setItems(prev =>
                    prev.map(it => (it.id === id ? { ...it, status: 'error', error: msg } : it))
                );
            };

            const uploadPromises = presignedList.map((presigned, index) => {
                const item = items[index];
                const file = files[index];

                const formData = new FormData();
                Object.entries(presigned.fields || {}).forEach(([k, v]) => {
                    formData.append(k, String(v));
                });
                formData.append('file', file);

                return axios.post(presigned.url, formData, {
                    onUploadProgress: (evt) => {
                        const total = evt.total || file.size || 1;
                        const loaded = evt.loaded || 0;
                        const pct = Math.max(0, Math.min(100, Math.round((loaded * 100) / total)));
                        updateProgress(item.id, pct);
                    },
                }).catch((err) => {
                    markError(item.id, 'Upload failed');
                    throw err;
                });
            });

            await Promise.all(uploadPromises);

            setStage('completing');
            await PathologyServices.completePathologyUpload(newUploadId);

            setItems(prev => prev.map(it => ({ ...it, progress: 100, status: 'done' })));
            setStage('done');

            toast.success('Pathology upload complete. Processing started.');
            onUploadFinished?.(newUploadId);
        } catch (err: any) {
            const msg = err?.message || 'Upload failed. Please try again.';
            setErrorMsg(msg);
            setStage('error');
            toast.error(msg);
        }
    };

    return (
        <>
            <Button
                variant="primary"
                size="small"
                onClick={() => setOpen(true)}
                leftIcon={<Upload style={{ width: 16, height: 16 }} />}
            >
                Upload Slides
            </Button>

            <Dialog
                isOpen={open}
                onClose={closeModal}
                size="large"
                title="Upload Pathology Files"
                description="Upload DICOM (.dcm) or ZIP (.zip) files for a selected patient."
                preventCloseOnOverlayClick={uploading}
                footer={
                    <div className="flex items-center justify-between gap-3">
                        <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                            {items.length > 0 ? `${items.length} file(s) selected` : 'No files selected'}
                        </div>

                        <div className="flex items-center gap-2">
                            <Button variant="ghost" onClick={clearFiles} disabled={uploading || items.length === 0}>
                                Clear
                            </Button>
                            <Button variant="outline" onClick={closeModal} disabled={uploading}>
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                onClick={startUpload}
                                disabled={uploading || items.length === 0 || !selectedPatient}
                                loading={uploading}
                            >
                                {uploading ? 'Uploading...' : 'Start Upload'}
                            </Button>
                        </div>
                    </div>
                }
            >
                <div className="space-y-4">


                    <div
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={onDrop}
                        style={{
                            border: '1px dashed var(--border-interactive)',
                            background: 'var(--bg-surface)',
                            borderRadius: 12,
                            padding: 16,
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                            <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                                Drag and drop files here or browse.
                                <br />
                                Only <strong>.dcm</strong> or <strong>.zip</strong>. Do not mix both types in one upload.
                            </div>
                            <input
                                ref={fileInputRef}
                                id="pathology-upload-files"
                                type="file"
                                multiple
                                accept={ACCEPT_ATTR}
                                onChange={(e) => {
                                    onPickFiles(e.target.files);
                                    e.target.value = '';
                                }}
                                disabled={uploading}
                                style={{ display: 'none' }}
                                aria-hidden
                            />
                            <Button
                                variant="secondary"
                                size="small"
                                type="button"
                                disabled={uploading}
                                onClick={() => fileInputRef.current?.click()}
                            >
                                Browse Files
                            </Button>
                        </div>
                    </div>

                    <div
                        style={{
                            border: '1px solid var(--border-subtle)',
                            background: 'var(--bg-card)',
                            borderRadius: 12,
                            maxHeight: 260,
                            overflow: 'auto',
                        }}
                    >
                        {items.length === 0 ? (
                            <div style={{ padding: 14, fontSize: 13, color: 'var(--text-muted)' }}>
                                No files added yet.
                            </div>
                        ) : (
                            items.map((it) => (
                                <div
                                    key={it.id}
                                    style={{
                                        padding: '10px 12px',
                                        borderBottom: '1px solid var(--border-subtle)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 10,
                                    }}
                                >
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                            {it.file.name}
                                        </div>
                                        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                                            {formatBytes(it.file.size)}
                                        </div>
                                        <div
                                            style={{
                                                marginTop: 6,
                                                height: 6,
                                                width: '100%',
                                                background: 'var(--bg-inset)',
                                                borderRadius: 999,
                                                overflow: 'hidden',
                                            }}
                                        >
                                            <div
                                                style={{
                                                    height: '100%',
                                                    width: `${it.progress}%`,
                                                    background:
                                                        it.status === 'error'
                                                            ? 'var(--danger)'
                                                            : 'linear-gradient(90deg, var(--v2-primary-500), var(--v2-primary-600))',
                                                    transition: 'width 160ms linear',
                                                }}
                                            />
                                        </div>
                                    </div>

                                    <div style={{ width: 86, textAlign: 'right', fontSize: 12, fontWeight: 700 }}>
                                        {it.status === 'done' && (
                                            <span style={{ color: 'var(--success)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                                <CheckCircle2 style={{ width: 14, height: 14 }} />
                                                Done
                                            </span>
                                        )}
                                        {it.status === 'error' && (
                                            <span style={{ color: 'var(--danger)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                                <AlertCircle style={{ width: 14, height: 14 }} />
                                                Error
                                            </span>
                                        )}
                                        {(it.status === 'pending' || it.status === 'uploading') && (
                                            <span style={{ color: 'var(--text-secondary)' }}>{it.progress}%</span>
                                        )}
                                    </div>

                                    <button
                                        type="button"
                                        onClick={() => removeItem(it.id)}
                                        disabled={uploading}
                                        aria-label={`Remove ${it.file.name}`}
                                        style={{
                                            border: 'none',
                                            background: 'transparent',
                                            color: 'var(--text-muted)',
                                            cursor: uploading ? 'not-allowed' : 'pointer',
                                            padding: 2,
                                        }}
                                    >
                                        <X style={{ width: 15, height: 15 }} />
                                    </button>
                                </div>
                            ))
                        )}
                    </div>

                    {(stageLabel || errorMsg) && (
                        <div
                            style={{
                                border: '1px solid var(--border-subtle)',
                                background: 'var(--bg-surface)',
                                borderRadius: 10,
                                padding: 10,
                            }}
                        >
                            {stageLabel && (
                                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)' }}>
                                    {stageLabel} {stage === 'uploading' ? `(${totalProgress}%)` : ''}
                                </div>
                            )}
                            {uploadId && (
                                <div style={{ marginTop: 4, fontSize: 12, color: 'var(--text-muted)' }}>
                                    Upload ID: <code>{uploadId}</code>
                                </div>
                            )}
                            {errorMsg && (
                                <div style={{ marginTop: 6, fontSize: 12, color: 'var(--danger)' }}>
                                    {errorMsg}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </Dialog>
        </>
    );
};

export default PathologyUploadModal;
